import {defineStore} from 'pinia';
import {ref} from 'vue';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';

export const useViewedContentStore =
  /**
   * Content the user opened during this session, overlaid on the backend's `consumed` flag.
   *
   * Explore keeps its rails alive and deliberately skips the refresh on a quick back navigation
   * (IPD-2938) so the restored scroll position does not shift under the user. `consumed` only
   * ever changes server-side, so without this the Read / Listened / Watched marker did not
   * appear until a full reload (IPD-3035).
   *
   * The overlay is monotonic — it only ever turns the marker ON. Viewing is irreversible, so it
   * cannot be wrong in the direction that matters (marking something read that is not); at worst
   * it agrees with the backend early. That is what makes it safe to render without a round trip.
   *
   * Session-scoped on purpose: a reload refetches, and the backend value is authoritative.
   *
   * It also holds what has already been reported as watched, for the same session and cleared by
   * the same logout: see `reportedWatchedIds` below.
   */
  defineStore('viewed-content-store', () => {
  // Replaced rather than mutated on each add, so the value stays a plain immutable snapshot and
  // every reader re-evaluates without relying on collection reactivity.
  const viewedIds = ref<ReadonlySet<UuidDTO>>(new Set());

  /**
   * Pieces already reported to the backend as watched (IPD-2980), which is a different question
   * from the one above: viewing is opening, this is having played half of it.
   * Kept here rather than in a store of its own because it has the same life and the same reason
   * to be cleared, and both are read while a piece of content is on screen.
   */
  const reportedWatchedIds = ref<ReadonlySet<UuidDTO>>(new Set());

  /**
   * Pieces already reported as presented (IPD-3114) — put in front of the reader rather than
   * played by them. Kept apart from the watch above because they answer different questions and
   * the backend records them separately; kept here for the same life and the same clearing.
   */
  const reportedPresentedIds = ref<ReadonlySet<UuidDTO>>(new Set());

  const markViewed = (id: UuidDTO): void => {
    if (viewedIds.value.has(id)) {
      return;
    }
    viewedIds.value = new Set(viewedIds.value).add(id);
  };

  // Optional: callers pass ids straight off card data, which is frequently absent.
  const hasViewed = (id?: UuidDTO | null): boolean => (id ? viewedIds.value.has(id) : false);

  const hasReportedWatched = (id: UuidDTO): boolean => reportedWatchedIds.value.has(id);

  const markReportedWatched = (id: UuidDTO): void => {
    reportedWatchedIds.value = new Set(reportedWatchedIds.value).add(id);
  };

  // A report that never arrived is not a report: the next viewing is free to try again.
  const forgetReportedWatched = (id: UuidDTO): void => {
    const remaining = new Set(reportedWatchedIds.value);
    remaining.delete(id);
    reportedWatchedIds.value = remaining;
  };

  const hasReportedPresented = (id: UuidDTO): boolean => reportedPresentedIds.value.has(id);

  const markReportedPresented = (id: UuidDTO): void => {
    reportedPresentedIds.value = new Set(reportedPresentedIds.value).add(id);
  };

  // Same reasoning as the watch: a report that never arrived is not a report.
  const forgetReportedPresented = (id: UuidDTO): void => {
    const remaining = new Set(reportedPresentedIds.value);
    remaining.delete(id);
    reportedPresentedIds.value = remaining;
  };

  const clearStore = (): void => {
    viewedIds.value = new Set();
    reportedWatchedIds.value = new Set();
    reportedPresentedIds.value = new Set();
  };

  return {
    viewedIds,
    markViewed,
    hasViewed,
    reportedWatchedIds,
    hasReportedWatched,
    markReportedWatched,
    forgetReportedWatched,
    reportedPresentedIds,
    hasReportedPresented,
    markReportedPresented,
    forgetReportedPresented,
    clearStore,
  };
});
